import React, { useEffect, useMemo, useRef, useState } from 'react';

type HomePreloaderProps = {
  onDone?: () => void;
};

// Helper to create a promise that resolves on a media element event
function once(el: HTMLMediaElement, event: keyof GlobalEventHandlersEventMap): Promise<void> {
  return new Promise((resolve) => {
    if (event === 'loadedmetadata') {
      // If metadata already available, resolve immediately
      if (el.readyState >= 1) return resolve();
    }
    const handler = () => {
      el.removeEventListener(event as any, handler);
      resolve();
    };
    el.addEventListener(event as any, handler, { once: true });
  });
}

export default function HomePreloader({ onDone }: HomePreloaderProps) {
  const [visible, setVisible] = useState(true);
  const [fading, setFading] = useState(false);
  const [bgSrc, setBgSrc] = useState<string>('/mainloader.mp4');
  const [videoLoading, setVideoLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const bgVideoRef = useRef<HTMLVideoElement | null>(null);
  const v1 = useRef<HTMLVideoElement | null>(null);
  const v2 = useRef<HTMLVideoElement | null>(null);
  const v3 = useRef<HTMLVideoElement | null>(null);
  const tiles = useMemo(() => [v1, v2, v3], []);

  // Cache to track which videos have been loaded
  const videoCache = useRef<Set<string>>(new Set(['/mainloader.mp4']));


  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Set default background to wedding video on mobile
  useEffect(() => {
    if (isMobile) {
      setBgSrc('/wedding.mp4');
      if (!videoCache.current.has('/wedding.mp4')) {
        videoCache.current.add('/wedding.mp4');
      }
    }
  }, [isMobile]);



  // Preload videos - skip on mobile to save bandwidth and improve initial load
  useEffect(() => {
    const bgVideo = bgVideoRef.current;
    if (!bgVideo || isMobile) return; // Skip preloading on mobile

    const preloadAllVideos = () => {
      // Only preload on desktop for smooth transitions
      const srcs = ['/mainloader.mp4', '/wedding.mp4', '/live.mp4', '/infra.mp4'];

      srcs.forEach((src) => {
        if (!videoCache.current.has(src)) {
          const preloadVideo = document.createElement('video');
          preloadVideo.src = src;
          preloadVideo.preload = 'metadata'; // Changed from 'auto' to 'metadata'
          preloadVideo.muted = true;
          preloadVideo.playsInline = true;

          preloadVideo.addEventListener('loadedmetadata', () => {
            videoCache.current.add(src);
            preloadVideo.remove();
          }, { once: true });
        }
      });
    };

    // Start preloading other videos once first background can play (desktop only)
    if (!isMobile) {
      if (bgVideo.readyState >= 2) {
        setTimeout(preloadAllVideos, 2000); // Increased delay
      } else {
        bgVideo.addEventListener('canplay', () => setTimeout(preloadAllVideos, 2000), { once: true });
      }
    }
  }, []);

  // Toggle play/pause for background video
  const togglePlayPause = () => {
    const el = bgVideoRef.current;
    if (!el) return;

    if (el.paused) {
      el.play().then(() => setIsPlaying(true)).catch(() => { });
    } else {
      el.pause();
      setIsPlaying(false);
    }
  };

  // Track video loading state
  useEffect(() => {
    const el = bgVideoRef.current;
    if (!el) return;

    const handleCanPlay = () => setVideoLoading(false);
    const handleWaiting = () => setVideoLoading(true);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    el.addEventListener('canplay', handleCanPlay);
    el.addEventListener('waiting', handleWaiting);
    el.addEventListener('playing', handlePlay);
    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handlePause);

    return () => {
      el.removeEventListener('canplay', handleCanPlay);
      el.removeEventListener('waiting', handleWaiting);
      el.removeEventListener('playing', handlePlay);
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('pause', handlePause);
    };
  }, [bgSrc]);
  // Ensure iOS autoplay compatibility: set playsinline & webkit-playsinline on all videos
  useEffect(() => {
    const allVideos: Array<React.RefObject<HTMLVideoElement | null>> = [bgVideoRef, ...tiles];
    allVideos.forEach((ref) => {
      const el = ref.current;
      if (!el) return;
      try {
        el.setAttribute('playsinline', 'true');
        el.setAttribute('webkit-playsinline', 'true');
        el.muted = true; // iOS requires muted for autoplay
      } catch { }
    });
  }, [tiles]);


  const playMuted = (idx: number) => {
    const el = tiles[idx].current;
    if (!el) return;
    el.muted = true;
    el.play().catch(() => { }); // Just play without seeking - faster on iOS
  };

  const pause = (idx: number) => {
    tiles[idx].current?.pause();
  };

  const togglePlayOnTouch = (idx: number) => {
    const el = tiles[idx].current;
    if (!el) return;

    // On mobile, prioritize switching background over playing tile
    if (isMobile) {
      setBackground(idx);
    } else {
      // Desktop behavior: toggle play on tile
      if (el.paused) {
        el.muted = true;
        el.play().catch(() => { });
      } else {
        el.pause();
      }
    }
  };

  const setBackground = (idx: number) => {
    const srcs = ['/wedding.mp4', '/live.mp4', '/infra.mp4'];
    const newSrc = srcs[idx];

    // If already showing this video, do nothing
    if (bgSrc === newSrc) return;

    // Check if video is already cached/loaded
    const isInCache = videoCache.current.has(newSrc);

    if (isInCache) {
      // Fast switch - video already loaded
      setBgSrc(newSrc);
      setVideoLoading(false);
    } else {
      // Show loading, then switch
      setVideoLoading(true);
      setBgSrc(newSrc);
      videoCache.current.add(newSrc);
    }


  };

  // Ensure background video plays after source change
  useEffect(() => {
    const el = bgVideoRef.current;
    if (!el) return;
    el.muted = true;
    el.volume = 0;
    // force inline playback for iOS
    el.setAttribute('playsinline', 'true');
    el.setAttribute('webkit-playsinline', 'true');
    // also set property for some browsers
    (el as any).playsInline = true;

    const tryPlay = () => {
      // Don't call load() - it causes re-download delay on iOS
      const p = el.play();
      if (p && typeof (p as any).catch === 'function') {
        (p as Promise<void>).catch(() => {
          // iOS blocked autoplay - will need user gesture
        });
      }
    };

    // Try to play as soon as possible
    // On mobile/iOS, wait for user interaction or use lighter loading
    if (isMobile) {
      // On mobile, wait for loadeddata (lighter than canplay)
      if (el.readyState >= 2) {
        tryPlay();
      } else {
        once(el, 'loadeddata').then(tryPlay);
      }
    } else {
      // Desktop: standard behavior
      if (el.readyState >= 2) {
        tryPlay();
      } else {
        once(el, 'canplay').then(tryPlay);
      }
    }
  }, [bgSrc]);

  // iOS fallback: ensure ANY user interaction triggers playback if autoplay was blocked
  useEffect(() => {
    const el = bgVideoRef.current;
    if (!el) return;

    const startPlayback = () => {
      try {
        if (el.paused) {
          el.muted = true;
          el.volume = 0;
          el.setAttribute('playsinline', 'true');
          el.setAttribute('webkit-playsinline', 'true');
          (el as any).playsInline = true;
          const p = el.play();
          if (p && typeof (p as any).catch === 'function') {
            (p as Promise<void>).catch(() => { });
          }
        }
      } catch { }
    };

    // Listen for multiple event types to catch first user interaction
    const events = ['touchstart', 'touchend', 'click', 'mousedown'];
    events.forEach(evt => {
      window.addEventListener(evt, startPlayback, { once: true, passive: true });
    });

    return () => {
      events.forEach(evt => {
        window.removeEventListener(evt, startPlayback);
      });
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] overflow-hidden ${fading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-700`}
      aria-live="polite"
      aria-busy="true"
    >
      {/* Background video */}
      <video
        key={bgSrc}
        ref={bgVideoRef}
        className="absolute inset-0 w-full h-full object-cover"
        src={bgSrc}
        muted
        loop
        autoPlay
        playsInline
        preload={isMobile ? "metadata" : "auto"}
        crossOrigin="anonymous"
        poster={bgSrc.replace('.mp4', '.png')}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/80" />
      <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/20 to-black/40" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(120,119,198,0.15),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(255,215,0,0.08),transparent_50%)]" />

      {/* Elegant top section with logo */}
      <div className="absolute top-0 left-0 right-0 z-20 pt-8 md:pt-10 pb-4">
        <div className="flex flex-col items-center gap-3">
          {/* Decorative top line */}
          <div className="flex items-center gap-2">
            <div className="h-px w-8 bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
            <div className="w-1 h-1 rounded-full bg-white/60 animate-pulse"></div>
            <div className="h-px w-8 bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
          </div>

          {/* Logo with premium backdrop */}
          <div className="relative group">
            <div className="absolute -inset-3 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
            <img src="/ypd.png" alt="YPD Weddings" className="relative h-12 md:h-14 lg:h-16 opacity-95 drop-shadow-2xl transition-all duration-500 group-hover:scale-105" />
          </div>

          {/* Tagline with enhanced styling */}
          <div className="flex flex-col items-center gap-1.5">
            <p className="text-white/70 text-[9px] md:text-[10px] tracking-[0.5em] uppercase font-light">
              Experience Excellence
            </p>
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
          </div>
        </div>
      </div>

      {/* Loading indicator - elegant design */}
      {videoLoading && (
        <div className="absolute top-8 right-8 z-20 flex items-center gap-3 backdrop-blur-md bg-black/20 px-4 py-2 rounded-full border border-white/10">
          <span className="text-white/60 text-[10px] tracking-[0.3em] uppercase font-light">Loading</span>
          <div className="w-4 h-4 border-2 border-white/20 border-t-white/80 rounded-full animate-spin"></div>
        </div>
      )}

      {/* Play/Pause button - only on mobile */}
      {isMobile && (
        <button
          onClick={togglePlayPause}
          className="absolute top-6 left-6 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-white/40 flex items-center justify-center backdrop-blur-sm bg-black/20 hover:bg-white/10 transition-all duration-300 group"
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
        >
          {isPlaying ? (
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white/80 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white/80 group-hover:text-white transition-colors ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
      )}

      {/* Bottom center horizontal tiles */}
      <div className="absolute bottom-[9rem] sm:bottom-36 md:bottom-44 left-1/2 -translate-x-1/2 z-10 w-full max-w-6xl px-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
          {/* Tile 1 */}
          <div className="group space-y-3 sm:space-y-4">
            <div
              className="relative overflow-hidden cursor-pointer rounded-lg border border-white/20 backdrop-blur-sm transition-all duration-500 hover:shadow-2xl hover:shadow-white/20 hover:border-white/40 hover:-translate-y-1"
              onClick={() => setBackground(0)}
              onMouseEnter={() => { if (!isMobile) setBackground(0); }}
            >
              {/* Premium corner accents */}
              <div className="absolute top-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-t-2 border-l-2 border-white/40 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 rounded-tl-lg" />
              <div className="absolute top-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-t-2 border-r-2 border-white/40 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 rounded-tr-lg" />
              <div className="absolute bottom-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-b-2 border-l-2 border-white/40 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 rounded-bl-lg" />
              <div className="absolute bottom-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-b-2 border-r-2 border-white/40 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 rounded-br-lg" />

              <img
                src="/decor_new.jpg"
                alt="YPD Weddings"
                className="w-full h-40 sm:h-48 md:h-56 lg:h-64 object-cover object-center transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-90 group-hover:opacity-50 transition-opacity duration-500" />

              {/* Subtle shine effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Hover overlay with icon */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-12 sm:w-14 h-12 sm:h-14 rounded-full border-2 border-white/90 flex items-center justify-center backdrop-blur-md bg-white/20 shadow-lg">
                  <svg className="w-5 sm:w-6 h-5 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="text-center">
              <p className="text-white/90 text-[10px] sm:text-xs md:text-sm tracking-[0.3em] sm:tracking-[0.4em] uppercase font-light group-hover:text-white group-hover:tracking-[0.5em] transition-all duration-300">YPD Weddings</p>
            </div>
          </div>

          {/* Tiles 2, 3 - horizontal layout */}
          {[
            { ref: v2, src: '/live.mp4', thumbnail: '/live_new.jpg', label: 'YPD Live', idx: 1 },
            { ref: v3, src: '/infra.mp4', thumbnail: '/infra.png', label: 'YPD Spaces', idx: 2 }
          ].map(({ ref, src, thumbnail, label, idx }) => (
            <div key={idx} className={`group space-y-3 sm:space-y-4 ${idx === 2 ? 'col-span-2 md:col-span-1' : ''}`}>
              <div
                className="relative overflow-hidden cursor-pointer rounded-lg border border-white/20 backdrop-blur-sm transition-all duration-500 hover:shadow-2xl hover:shadow-white/20 hover:border-white/40 hover:-translate-y-1"
                onClick={() => setBackground(idx)}
                onMouseEnter={() => { if (!isMobile) setBackground(idx); }}
              >
                {/* Premium corner accents */}
                <div className="absolute top-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-t-2 border-l-2 border-white/40 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-t-2 border-r-2 border-white/40 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-b-2 border-l-2 border-white/40 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-b-2 border-r-2 border-white/40 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 rounded-br-lg" />

                <img
                  src={thumbnail}
                  alt={label}
                  className="w-full h-40 sm:h-48 md:h-56 lg:h-64 object-cover object-center transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-90 group-hover:opacity-50 transition-opacity duration-500" />

                {/* Subtle shine effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-12 sm:w-14 h-12 sm:h-14 rounded-full border-2 border-white/90 flex items-center justify-center backdrop-blur-md bg-white/20 shadow-lg">
                    <svg className="w-5 sm:w-6 h-5 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                </div>
              </div>
              <div className="text-center"><p className="text-white/90 text-[10px] sm:text-xs md:text-sm tracking-[0.3em] sm:tracking-[0.4em] uppercase font-light group-hover:text-white group-hover:tracking-[0.5em] transition-all duration-300">{label}</p></div>
            </div>
          ))}
        </div>
      </div>

      {/* Center CTA Button */}
      <div className="absolute bottom-6 sm:bottom-8 md:bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 sm:gap-4">

        {/* Decorative top ornament */}
        <div className="flex items-center gap-3">
          <div className="h-px w-10 bg-gradient-to-r from-transparent via-white/40 to-white/20"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse shadow-lg shadow-white/50"></div>
          <div className="h-px w-10 bg-gradient-to-l from-transparent via-white/40 to-white/20"></div>
        </div>

        <button
          onClick={() => {
            setFading(true);
            setTimeout(() => {
              setVisible(false);
              onDone?.();
            }, 700);
          }}
          className="group relative px-6 sm:px-8 md:px-10 py-2.5 sm:py-3 border-2 border-white/50 rounded-full text-white text-[9px] sm:text-[10px] tracking-[0.4em] sm:tracking-[0.5em] uppercase font-light bg-white/5 hover:bg-white/15 hover:border-white/80 transition-all duration-500 backdrop-blur-xl overflow-hidden shadow-xl hover:shadow-white/20 hover:scale-105"
        >
          {/* Animated gradient background */}
          <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

          {/* Glow effect */}
          <span className="absolute inset-0 rounded-full blur-xl bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          <span className="relative z-10 flex items-center gap-2">
            Enter Website
            <svg className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </span>
        </button>

        {/* Bottom tagline */}
        <div className="flex flex-col items-center gap-2">
          <div className="h-px w-16 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
          <p className="text-white/50 text-[8px] sm:text-[9px] tracking-[0.3em] sm:tracking-[0.4em] uppercase font-light">Where Memories Begin</p>
        </div>
      </div>
    </div>
  );
}